'use client';

import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X, Loader2, Check } from 'lucide-react';
import { TEAM_MEMBERS, type Project, type ProjectStatus } from '@/lib/mockData';
import { toast } from 'sonner';

interface FormValues {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  status: ProjectStatus;
  teamMemberIds: string[];
}

interface CreateProjectModalProps {
  onClose: () => void;
  onCreate: (data: Omit<Project, 'id' | 'tasks' | 'progress'>) => void;
}

export default function CreateProjectModal({ onClose, onCreate }: CreateProjectModalProps) {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      description: '',
      startDate: '2026-05-27',
      endDate: '',
      status: 'Active',
      teamMemberIds: [],
    },
  });

  const selectedMembers = watch('teamMemberIds');

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    // Backend integration point: POST /api/projects
    await new Promise((r) => setTimeout(r, 900));
    onCreate(data);
    toast.success('Project created successfully');
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop">
      <div className="bg-card rounded-2xl border border-border w-full max-w-lg shadow-modal fade-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-foreground">Create New Project</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Fill in the details to get started</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5 overflow-y-auto max-h-[70vh] scrollbar-thin">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('name', { required: 'Project name is required' })}
              placeholder="e.g. Q3 Product Launch"
              className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground"
            />
            {errors.name && (
              <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Description
            </label>
            <p className="text-xs text-muted-foreground mb-1.5">
              Briefly describe the project scope and goals
            </p>
            <textarea
              {...register('description')}
              rows={3}
              placeholder="What is this project about?"
              className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground resize-none"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('startDate', { required: 'Start date is required' })}
                className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
              {errors.startDate && (
                <p className="text-xs text-red-500 mt-1">{errors.startDate.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('endDate', { required: 'End date is required' })}
                className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
              {errors.endDate && (
                <p className="text-xs text-red-500 mt-1">{errors.endDate.message}</p>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Status</label>
            <select
              {...register('status')}
              className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all"
            >
              <option value="Active">Active</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
            </select>
          </div>

          {/* Team Members */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Team Members
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Select team members to add to this project
            </p>
            <Controller
              name="teamMemberIds"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  {TEAM_MEMBERS.map((member) => {
                    const isSelected = field.value.includes(member.id);
                    return (
                      <button
                        key={`member-select-${member.id}`}
                        type="button"
                        onClick={() => {
                          const next = isSelected
                            ? field.value.filter((id) => id !== member.id)
                            : [...field.value, member.id];
                          field.onChange(next);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all duration-150 text-left ${
                          isSelected
                            ? 'border-primary/40 bg-primary/5' :'border-border bg-background hover:bg-muted/50'
                        }`}
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                          style={{ backgroundColor: member.avatarColor }}
                        >
                          {member.avatarInitials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.role}</p>
                        </div>
                        {isSelected && (
                          <Check size={16} className="text-primary flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            form="create-project-form"
            onClick={handleSubmit(onSubmit)}
            disabled={loading}
            className="btn-primary flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium min-w-[130px] justify-center disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Creating...
              </>
            ) : (
              'Create Project'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}